import { useState, useRef } from 'react';
import './Newsletter.scss';
import { useContentful } from '../contentful/ContentContext';

export default function NewsletterSignup({
  customText,
}: {
  customText?: string;
}) {
  const { getUiText } = useContentful();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({
    required: false,
    invalid: false,
    tooLong: false,
  });
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const validateAndSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Reset errors
    setErrors({ required: false, invalid: false, tooLong: false });

    // Check if email is empty
    if (!email || email.trim() === '') {
      setErrors((prev) => ({ ...prev, required: true }));
      e.preventDefault();
      return false;
    }

    // Check if email is valid (using the same regex as the original)
    const emailRegex =
      /^(?:[A-Za-z0-9!#$%&\'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*|""(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*"")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;

    if (!emailRegex.test(email)) {
      setErrors((prev) => ({ ...prev, invalid: true }));
      e.preventDefault();
      return false;
    }

    // Check if email is too long (max 400 characters, no newlines/returns)
    if (!/^[^\n\r]{0,400}$/.test(email)) {
      setErrors((prev) => ({ ...prev, tooLong: true }));
      e.preventDefault();
      return false;
    }

    // If validation passes, disable submit button and allow form to submit normally
    if (submitButtonRef.current) {
      submitButtonRef.current.disabled = true;
    }

    // Don't prevent default - let the form submit to Paloma
    return true;
  };

  return (
    <div className="newsletterSignup">
      {customText && <h3 className="heading">{customText}</h3>}
      <form
        name="SubscribeForm"
        onSubmit={validateAndSubmit}
        action="https://public.paloma.se/subscription/register"
        method="POST"
        target="_blank"
        rel="noopener noreferrer">
        <input
          type="hidden"
          name="FormKey"
          value="d186d0ed-db70-411b-9422-dc6e3eece130"
        />
        <input type="hidden" name="Language" value="sv" />
        <input type="hidden" name="LegalBasis" value="0" />
        <input type="hidden" name="OptInSenderName" value="Urjord keramik" />
        <input
          type="hidden"
          name="OptInSenderEmail"
          value="urjordkeramik@sentwithpaloma.se"
        />
        <input
          type="hidden"
          name="OptInMessage"
          value="SGVqITxici8+PGJyLz5EdSBm5XIgZGV0dGEgbWVkZGVsYW5kZSBlZnRlcnNvbSBkaW4gZS1wb3N0IHJlZ2lzdHJlcmFkZXMgdmlhIHblcnQgcmVnaXN0cmVyaW5nc2Zvcm11bORyLjxici8+PGJyLz5G9nIgYXR0IGJla3LkZnRhIGRldHRhIG9jaCBhbm3kbGEgZGluIGFkcmVzcyA8YSBocmVmPSJbLy9hY3RpdmF0aW9udXJsXSI+a2xpY2thIGjkcjwvYT4sIGVsbGVyIGtsaXN0cmEgaW4gZGV0dGEgaSBkaW4gd2ViYmzkc2FyZTo8YnIvPlsvL2FjdGl2YXRpb251cmxdPGJyLz48YnIvPk9tIGR1IGludGUgYmVzdORsbHQgZGV0dGEga2FuIGR1IGJhcmEgYm9ydHNlIGZy5W4gZGV0dGEgbWVkZGVsYW5kZSBz5SBrb21tZXIgaW5nZW4gcmVnaXN0cmVyaW5nIGF0dCBza2UuPGJyLz4="
        />
        <input type="hidden" name="ThanksPage" value="" />
        <input type="hidden" name="CheckThanksPageAvailability" value="0" />
        <div className="formGrid">
          <div className="formGroup">
            <label htmlFor="SubscriberForm_email">Epost</label>
            <input
              type="email"
              id="SubscriberForm_email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            {errors.required && <span>{getUiText('UI.RequiredField')}</span>}
            {errors.invalid && <span>{getUiText('UI.InvalidEmail')}</span>}
            {errors.tooLong && <span>{getUiText('UI.MaxLengthExceeded')}</span>}
          </div>

          <button
            className="submitButton"
            ref={submitButtonRef}
            type="submit"
            id="SubscriberForm_SubmitButton">
            {getUiText('UI.Subscribe')}
          </button>
        </div>
      </form>
    </div>
  );
}
